import React from "react";
import type { ModalViewType, ModelFilterCarType } from "../../../../../../common-deprecated/types/CommonTypes";
import * as Modal from "../../../../../../common-deprecated/styles/v2/toyota/globals/Modal";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import * as StatusStyles from "../../../../../../common-deprecated/components/modals/status/styles/StatusStyles";
import ModalBaseView from "../../../../../../common-deprecated/components/modals/aem/ModalBaseView";
import type { ModalFilterDebugType } from "../../../utils/modalConstants";
import { useCommonLabel } from "../../../../../../common-deprecated/utils/commonLabels";
import IconWrapper from "../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import { useBnbSelector } from "../../../redux/store";
import type { StatusEntryType } from "../../../../../../common-deprecated/components/modals/status/StatusModalEntry";
import StatusModalEntry from "../../../../../../common-deprecated/components/modals/status/StatusModalEntry";
import { modelIsLcv } from "../../../../../shared-logic/utils/lcvUtils";

export const FilterDebugModal = (props: ModalViewType<ModalFilterDebugType>): JSX.Element => {
    const { show, close, modalSettings } = props;
    const closeLabel = useCommonLabel("close");
    const { cars, filters } = useBnbSelector(({ modelFilter }) => modelFilter);
    const modelId = useBnbSelector((state) => state.model.id);
    const isLcv = modelIsLcv(modelId);
    const hasFilters = !!filters.length;

    // Check if each car actually has all filters defined, if not, log a message.
    const [validCars, invalidCars] = cars.reduce(
        ([valid, invalid]: [ModelFilterCarType[], ModelFilterCarType[]], car) => {
            const carIsValid = filters.every((filter) =>
                car.filterValues.find((carFilterValue) => filter.code === carFilterValue.code),
            );
            return carIsValid ? [[...valid, car], invalid] : [valid, [...invalid, car]];
        },
        [[], []],
    );

    const getFilterValueLabel = (filterCode: string, filterValueId: string): string => {
        const filter = filters.find(({ code }) => code === filterCode);
        const value = filter?.values.find((val) => val.id === filterValueId);
        return value?.label || "";
    };

    const configurationEntries: (StatusEntryType & { key: string })[] = [
        {
            key: "default-config",
            title: "Default Filter configuration",
            description: "All default filters should be configured.",
            values: filters.map((filter) => ({
                label: `${filter.code}: ${filter.label}`,
                value: filter.default,
                long: true,
                collapsibleContent: JSON.stringify(
                    {
                        defaultID: filter.default,
                        defaultValue: filter.values.find((val) => val.id === filter.default)?.label,
                        values: filter.values.reduce(
                            (dict: Record<string, string>, val) => ({
                                ...dict,
                                [val.id]: val.label,
                            }),
                            {},
                        ),
                    },
                    undefined,
                    2,
                ),
            })),
        },
        ...invalidCars.map((car) => ({
            key: `${car.id}-${car.filterValues.map(({ id }) => id).join("-")}`,
            title: car.name,
            description: "This vehicle has missing filters.",
            values: filters.map((filter) => {
                const filterValue = car.filterValues.find((carFilterValue) => filter.code === carFilterValue.code);
                return {
                    label: `${filter.code}: ${filter.label}`,
                    value: filterValue
                        ? `Present: ${getFilterValueLabel(filter.code, filterValue.id) || ""}`
                        : `Missing: ${filter.code}`,
                };
            }),
        })),
        ...validCars.map((car) => ({
            key: `${car.id}-${car.filterValues.map(({ id }) => id).join("-")}`,
            title: car.name,
            description: "This vehicle is fully configured.",
            values: filters.map((filter) => {
                const filterValue = car.filterValues.find((carFilterValue) => filter.code === carFilterValue.code);
                return {
                    label: `${filter.code}: ${filter.label}`,
                    value: `Present: ${(filterValue && getFilterValueLabel(filter.code, filterValue.id)) || ""}`,
                };
            }),
        })),
    ];

    const entries: StatusEntryType[] = [
        {
            key: "root-config",
            title: "Configuration",
            description: "Take an validate your Model configuration and filters.",
            values: [
                {
                    label: "Model can have Filters",
                    value: isLcv,
                },
                {
                    label: "Model has Filters",
                    value: hasFilters,
                },
                ...(hasFilters
                    ? [
                          {
                              label: "Amount of Cars with missing filters",
                              value: `${invalidCars.length}`,
                          },
                          {
                              label: "Amount of Cars with no missing filters",
                              value: `${validCars.length}`,
                          },
                      ]
                    : []),
            ],
        },
        ...(hasFilters ? configurationEntries : []),
    ];
    return (
        <ModalBaseView modalIdType={modalSettings.type} onClose={close} show={show} size="md">
            <Modal.Header>
                <Modal.Title>Model Filter Configuration</Modal.Title>
                <Modal.Button type="button" aria-label={closeLabel} onClick={close}>
                    <IconWrapper aria-hidden="true">
                        <Icon variant="close" />
                    </IconWrapper>
                </Modal.Button>
            </Modal.Header>
            <StatusStyles.Wrapper>
                {entries.map((entry, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>
                        <StatusModalEntry entry={entry} />
                    </React.Fragment>
                ))}
            </StatusStyles.Wrapper>
        </ModalBaseView>
    );
};
